import { t } from 'i18next';
import * as yup from 'yup';

import { LENGTH_XL, LENGTH_2048, LENGTH_10000 } from '../constants';
import { saveAsRefRegExp } from '../patterns';
import { PARTIAL_MATCH_INCOMPATIBLE_TYPES, PARTIAL_MATCH_METHODS } from './auth-constants';

const contextPathRegExp = /^\$[\w][\w-]*(?:\.[\w-]+)*$/i;

// ── Question validation (per-method) ──────────────────────────────

const GROUND_TRUTH_METHODS = new Set(['exact_match', 'fuzzy_match', ...PARTIAL_MATCH_METHODS]);

const authQuestionValidationSchema = yup.object().shape({
  method: yup
    .string()
    .oneOf([
      'exact_match',
      'fuzzy_match',
      'contains',
      'starts_with',
      'ends_with',
      'guideline',
      'webhook',
    ])
    .required(() => t('validation.required')),

  ground_truth: yup
    .string()
    .when('method', {
      is: (m: string) => GROUND_TRUTH_METHODS.has(m),
      then: (schema) =>
        schema
          .matches(contextPathRegExp, () => t('dialog.auth.save_as_invalid'))
          .required(() => t('validation.required')),
      otherwise: (schema) => schema.notRequired().strip(),
    }),

  fuzzy_match_threshold: yup
    .number()
    .when('method', {
      is: 'fuzzy_match',
      then: (schema) =>
        schema
          .min(0, () => t('validation.min_value', { 0: 0 }))
          .max(100, () => t('validation.max_value', { 0: 100 })),
      otherwise: (schema) => schema.notRequired().strip(),
    }),

  match_size: yup
    .number()
    .integer()
    .when('method', {
      is: (m: string) => PARTIAL_MATCH_METHODS.has(m),
      then: (schema) =>
        schema
          .min(1, () => t('validation.min_value', { 0: 1 }))
          .max(10, () => t('validation.max_value', { 0: 10 })),
      otherwise: (schema) => schema.notRequired().strip(),
    }),

  guideline: yup
    .string()
    .when('method', {
      is: 'guideline',
      then: (schema) =>
        schema
          .max(LENGTH_2048, () => t('validation.less_than', { 0: LENGTH_2048 }))
          .required(() => t('validation.required')),
      otherwise: (schema) => schema.notRequired().strip(),
    }),

  webhook_id: yup
    .string()
    .when('method', {
      is: 'webhook',
      then: (schema) => schema.required(() => t('validation.required')),
      otherwise: (schema) => schema.notRequired().strip(),
    }),
});

// ── Single question ───────────────────────────────────────────────

export const authQuestionSchema = yup
  .object()
  .shape({
    question_id: yup.string().required(),
    question: yup
      .string()
      .min(1, () => t('validation.required'))
      .max(LENGTH_XL, () => t('validation.less_than', { 0: LENGTH_XL }))
      .required(() => t('validation.required')),
    save_as: yup
      .string()
      .matches(saveAsRefRegExp, () => t('dialog.auth.save_as_invalid'))
      .required(() => t('validation.required')),
    type: yup
      .string()
      .oneOf(['text', 'date', 'integer', 'number', 'phone', 'email', 'alphanumeric'])
      .required(() => t('validation.required')),
    points: yup
      .number()
      .min(1, () => t('validation.min_value', { 0: 1 }))
      .max(5, () => t('validation.max_value', { 0: 5 }))
      .required(() => t('validation.required')),
    validation: authQuestionValidationSchema.required(),
  })
  .test(
    'partial-match-question-type',
    () => t('dialog.auth.validation_type_unsupported'),
    (value) => {
      if (!value) {
        return true;
      }
      const method = value.validation?.method;
      if (!method || !PARTIAL_MATCH_METHODS.has(method)) {
        return true;
      }
      return !PARTIAL_MATCH_INCOMPATIBLE_TYPES.has(value.type ?? '');
    }
  );

// ── Step failure ──────────────────────────────────────────────────

export const authStepFailureSchema = yup.object().shape({
  max_attempts: yup
    .number()
    .nullable()
    .min(1, () => t('validation.min_value', { 0: 1 }))
    .max(20, () => t('validation.max_value', { 0: 20 })),
  guideline: yup
    .string()
    .nullable()
    .max(LENGTH_2048, () => t('validation.less_than', { 0: LENGTH_2048 })),
  max_step_minutes: yup
    .number()
    .nullable()
    .min(1, () => t('validation.min_value', { 0: 1 }))
    .max(60, () => t('validation.max_value', { 0: 60 })),
});

// ── Auth step (identification or verification) ───────────────────

export const authStepSchema = yup.object().shape({
  enabled: yup.boolean().required(),
  questions: yup.array().when('enabled', {
    is: true,
    then: (schema) =>
      schema
        .of(authQuestionSchema)
        .min(1, () => t('dialog.auth.validation_min_questions'))
        .max(10, () => t('validation.max_items', { 0: 10 })),
    otherwise: (schema) => schema.notRequired(),
  }),
  success_points_threshold: yup
    .number()
    .when('enabled', {
      is: true,
      then: (schema) =>
        schema
          .min(1, () => t('validation.min_value', { 0: 1 }))
          .max(25, () => t('validation.max_value', { 0: 25 })),
      otherwise: (schema) => schema.notRequired(),
    }),
  guideline: yup
    .string()
    .nullable()
    .max(LENGTH_10000, () => t('validation.less_than', { 0: LENGTH_10000 })),
  failure: yup
    .object()
    .when('enabled', {
      is: true,
      then: () => authStepFailureSchema.required(),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  pre_enter_webhook_id: yup.string().nullable().notRequired(),
});

// ── Full auth config ─────────────────────────────────────────────

export const authConfigSchema = yup.object().shape({
  trigger: yup.object().shape({
    when: yup
      .string()
      .oneOf(['conversation_starts', 'campaign_starts', 'time_based'])
      .required(() => t('validation.required')),
  }),
  identification_step: authStepSchema.nullable().notRequired(),
  verification_step: authStepSchema.nullable().notRequired(),
  success_node_id: yup.string().notRequired(),
  failure_node_id: yup.string().notRequired(),
});
